import React, { useCallback, useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Popper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { styled } from "@mui/material/styles";
import { Clear, ExpandMore } from "@mui/icons-material";
import { useImmer } from "use-immer";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import i18next from "i18next";
import "dayjs/locale/fr";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { crmService } from "../../services/crm.service";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { updateCrmFilters } from "../../reducers/myDesigns/myDesignsSlice";
import { userService } from "../../services";
import { enrichDataWithDesignInfo } from "../../views/myDesigns/MyDesigns";

const PREFIX = "searchfilter";

const classes = {
  root: `${PREFIX}-root`,
  firstAccordionStyle: `${PREFIX}-firstAccordionStyle`,
  textAndIconStyle: `${PREFIX}-textAndIconStyle`,
  prospectSelectStyle: `${PREFIX}-prospectSelectStyle`,
  defaultDiv: `${PREFIX}-defaultDiv`,
  buttonDiv: `${PREFIX}-buttonDiv`,
  tagSelect: `${PREFIX}-tagSelect`,
  marginLeftButton: `${PREFIX}-marginLeftButton`,
  customClearIcon: `${PREFIX}-customClearIcon`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      width: "100%",
      margin: "0 1rem",
    },
    [`& .${classes.firstAccordionStyle}`]: {
      width: "100%",
      marginTop: "2rem",
      backgroundColor: "#303030",
      "&:last-of-type": {
        marginBottom: "2rem !important",
      },
    },
    [`& .${classes.prospectSelectStyle}`]: {
      width: "40%",
    },
    [`& .${classes.defaultDiv}`]: {
      display: "flex",
      justifyContent: "space-evenly",
      width: "100%",
      marginBottom: "1rem",
    },
    [`& .${classes.textAndIconStyle}`]: {
      color: "#D3AF5F",
    },
    [`& .${classes.buttonDiv}`]: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },
    [`& .${classes.tagSelect}`]: {
      width: "86.8%",
    },
    [`& .${classes.marginLeftButton}`]: {
      marginLeft: "2rem",
    },
    [`& .${classes.customClearIcon}`]: {
      color: "rgb(204, 204, 204)",
      transform: "translate(-1px, 3px)",
    },
    [`& .react-select__input`]: {
      color: "white !important",
    },
    [`& .react-select__menu`]: {
      zIndex: "9999 !important",
    },
  };
});

export default function FiltersSearch({
  setFilteredMyDesigns,
  setFilteringDesigns,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const crmFilters = useSelector((state) => state.myDesigns?.crmFilters);
  const [debounce, setDebounce] = useState({});
  const [accordionExpand, setAccordionExpand] = useState(true);
  const [loadingTags, setLoadingTags] = useState(false);
  const [filterParams, setFilterParams] = useImmer(filterParamsDefault());
  const [allManagers, setAllManagers] = useState([]);
  const filtersInitialized = useRef(false);

  const excludeFalseValue = useCallback((obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== false)
    );
  }, []);

  const handleFilter = useCallback(
    (refresh = true, crmFilters = null) => {
      setFilteringDesigns(true);
      crmService
        .searchCrm(crmFilters || excludeFalseValue(filterParams))
        .then((res) => {
          if (res.data.length > 0) {
            const enrichedResponse = enrichDataWithDesignInfo(res);
            setFilteredMyDesigns(enrichedResponse.data);
          }
          if (refresh) {
            lock.current = true;
            dispatch(updateCrmFilters(filterParams));
          }
        })
        .catch((err) => {
          console.log("err =>", err);
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        })
        .finally(() => setFilteringDesigns(false));
    },
    [
      dispatch,
      enqueueSnackbar,
      filterParams,
      setFilteredMyDesigns,
      setFilteringDesigns,
      t,
      excludeFalseValue,
    ]
  );

  const userSuperAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.isSuperAdmin
  );

  const lock = useRef(false);

  const loadData = useCallback(() => {
    if (userSuperAdmin === 1) {
      userService
        .getOnlyAdmins(0, null, {
          login: "",
          email: "",
          platform: "",
          sortModel: {
            field: "createdDate",
            sort: "desc",
          },
        })
        .then((result) => {
          setAllManagers(() => result.result);
        });
    } else {
      return;
    }
  }, [userSuperAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (crmFilters && !lock.current) {
      setFilterParams(crmFilters);
      handleFilter(false, crmFilters);
    }
  }, [crmFilters, handleFilter, setFilterParams]);

  useEffect(() => {
    const { cb, delay } = debounce;
    if (cb) {
      const timeout = setTimeout(cb, delay);
      return () => clearTimeout(timeout);
    }
  }, [debounce]);

  function filterParamsDefault() {
    return {
      prospect: false,
      client: false,
      frame: false,
      ar: false,
      poster: false,
      videoSimu: false,
      references: false,
      managers: [],
      soldDateStart: "",
      soldDateEnd: "",
      nationality: [],
      frameType: [],
      activity: [],
      brand: [],
    };
  }

  function handleResetFilter() {
    document.getElementById("clearIcon")?.click();
    setFilterParams(filterParamsDefault());
    handleFilter(true, filterParamsDefault());
  }

  const promiseOptions = (value, callback) => {
    setLoadingTags(true);
    setDebounce({
      cb: async () => {
        const tmpTagResult = await crmService.searchAllTags(value);
        setLoadingTags(false);
        const tmp = tmpTagResult
          .filter((item) => item.hasOwnProperty("type"))
          .map((element) => ({
            ...element,
            label: element.name,
            value: element.name,
          }));
        callback(tmp);
      },
      delay: 500, //ms
    });
  };

  const CustomerPopper = (props) => {
    const modifiers = [
      {
        name: "flip",
        options: {
          fallbackPlacements: [],
        },
      },
    ];

    return (
      <Popper
        {...props}
        modifiers={modifiers}
        popperOptions={{
          placement: "bottom",
        }}
      />
    );
  };

  useEffect(() => {
    if (filtersInitialized.current) {
      handleFilter();
    } else {
      filtersInitialized.current = true;
    }
  }, [filterParams, handleFilter]);

  return (
    <StyledDiv>
      <Accordion
        className={classes.firstAccordionStyle}
        onClick={() => {
          setAccordionExpand(!accordionExpand);
        }}
        expanded={accordionExpand}
      >
        <AccordionSummary
          expandIcon={<ExpandMore className={classes.textAndIconStyle} />}
          aria-controls="myDesigns-filter-content"
          id="myDesigns-filter-header"
        >
          <Typography className={classes.textAndIconStyle}>
            {t("Crm.filterTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails onClick={(e) => e.stopPropagation()}>
          <LimitedSelectionGroup
            title="Client Type"
            filterParams={filterParams}
            options={[
              { value: "prospect", label: "Propspect" },
              { value: "client", label: "Client" },
            ]}
            onChange={setFilterParams}
          />
          <LimitedSelectionGroup
            title="Type"
            filterParams={filterParams}
            options={[
              { value: "frame", label: t("Crm.frame") },
              { value: "ar", label: "AR" },
              { value: "poster", label: "Poster" },
            ]}
            onChange={setFilterParams}
          />
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={classes.defaultDiv}
          >
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18next.language.substring(0, 2)}
            >
              <DatePicker
                disableFuture
                label={t("Crm.startDate")}
                className={classes.prospectSelectStyle}
                onChange={(e) => {
                  setFilterParams((draft) => {
                    draft.soldDateStart = dayjs(e).format("YYYY-MM-DD");
                  });
                }}
                value={
                  filterParams.soldDateStart
                    ? dayjs(filterParams.soldDateStart)
                    : null
                }
              />
            </LocalizationProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18next.language.substring(0, 2)}
            >
              <DatePicker
                disableFuture
                label={t("Crm.endDate")}
                className={classes.prospectSelectStyle}
                onChange={(e) => {
                  setFilterParams((draft) => {
                    draft.soldDateEnd = dayjs(e).format("YYYY-MM-DD");
                  });
                }}
                value={
                  filterParams.soldDateEnd
                    ? dayjs(filterParams.soldDateEnd)
                    : null
                }
              />
            </LocalizationProvider>
          </div>
          <LimitedSelectionGroup
            title="Content Type"
            filterParams={filterParams}
            options={[
              { value: "videoSimu", label: "Video Simu" },
              { value: "references", label: "References" },
            ]}
            onChange={setFilterParams}
          />
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={classes.defaultDiv}
          >
            <Autocomplete
              sx={{ width: "86.8%" }}
              label={t("Crm.adminClient.adminClientManager")}
              id="autocomplete-users-client-admin"
              disableClearable
              PopperComponent={(props) => <CustomerPopper {...props} />}
              multiple
              options={allManagers}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              getOptionLabel={(option) => option.login}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Crm.adminClient.adminClientManager")}
                  variant="outlined"
                />
              )}
              defaultValue={filterParams.managers}
              value={filterParams.managers}
              className={classes.managerStyle}
              onChange={(e, value) => {
                setFilterParams((draft) => {
                  draft.managers = value;
                });
              }}
            />
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={classes.defaultDiv}
          >
            <AsyncSelect
              isMulti
              cacheOptions
              placeholder={"Tags"}
              isLoading={loadingTags}
              getOptionLabel={(option) => {
                return `[${t(`Crm.${option.type}`)}]: ${option.name}`;
              }}
              noOptionsMessage={() => "no result"}
              loadingMessage={() => "loading"}
              className={`react-select-container ${classes.tagSelect}`}
              classNamePrefix="react-select"
              components={{
                ClearIndicator: (props) => {
                  return (
                    <div
                      id="clearIcon"
                      onClick={() => {
                        setFilterParams({
                          ...filterParams,
                          nationality: [],
                          frameType: [],
                          activity: [],
                          brand: [],
                        });
                        props.clearValue();
                      }}
                    >
                      <Clear className={classes.customClearIcon} />
                    </div>
                  );
                },
              }}
              loadOptions={(value, callback) => promiseOptions(value, callback)}
              onClick={(e) => e.stopPropagation()}
              onChange={(newValue, actionMeta) => {
                if (
                  actionMeta.action !== "pop-value" &&
                  actionMeta.action !== "remove-value" &&
                  actionMeta.action !== "clear"
                ) {
                  const tmp = [...filterParams[actionMeta.option.type]];
                  tmp.push(actionMeta.option);
                  setFilterParams({
                    ...filterParams,
                    [actionMeta.option.type]: tmp,
                  });
                } else if (actionMeta.action === "clear") {
                  setFilterParams({
                    ...filterParams,
                    nationality: [],
                    frameType: [],
                    activity: [],
                    brand: [],
                  });
                } else {
                  const tmp = [...filterParams[actionMeta.removedValue.type]];
                  tmp.splice(
                    tmp.findIndex(
                      (item) => item.id === actionMeta.removedValue.id
                    ),
                    1
                  );
                  setFilterParams({
                    ...filterParams,
                    [actionMeta.removedValue.type]: tmp,
                  });
                }
              }}
            />
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={classes.buttonDiv}
          >
            {/* <Button
              variant="contained"
              startIcon={<SortIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleFilter();
              }}
            >
              {t("Crm.filter")}
            </Button> */}
            <Button
              variant="contained"
              className={classes.marginLeftButton}
              startIcon={<FilterListOffIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleResetFilter();
              }}
            >
              {t("Crm.filterReset")}
            </Button>
          </div>
        </AccordionDetails>
      </Accordion>
    </StyledDiv>
  );
}

const LimitedSelectionGroup = ({
  title,
  options,
  filterParams, // Pass the whole filterParams object
  onChange, // Function to update filterParams
  spacing = "2rem",
}) => {
  // Convert boolean flags to array for ToggleButtonGroup
  const getSelectedValues = () => {
    return options
      .filter((option) => filterParams[option.value] === true)
      .map((option) => option.value);
  };

  const handleSelection = (event, newSelection) => {
    // Create updated filterParams
    const updatedParams = { ...filterParams };

    // First, set all options to false
    options.forEach((option) => {
      updatedParams[option.value] = false;
    });

    // Then set selected options to true
    newSelection.forEach((selected) => {
      updatedParams[selected] = true;
    });

    // Update the filterParams
    onChange(updatedParams);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        mb: spacing,
        mt: 2,
      }}
    >
      <Typography variant="body1" sx={{ mb: 1, textAlign: "center" }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
        }}
      >
        <ToggleButtonGroup
          value={getSelectedValues()}
          onChange={handleSelection}
          aria-label={`${title} selection`}
          sx={{
            "& .MuiToggleButtonGroup-grouped": {
              border: "1px solid",
              borderColor: "divider",
              mx: 0.5,
              borderRadius: 1,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            },
          }}
        >
          {options.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              aria-label={option.value.toLowerCase()}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};
